import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Loader2,
  Save,
  Upload,
  Download,
  Trash2,
  Plus,
  FileText,
  StickyNote,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABEL = {
  new_enquiry: "New enquiry",
  under_review: "Under review",
  discovery_booked: "Discovery meeting booked",
  awaiting_documents: "Awaiting documents",
  funding_discussion: "Funding discussion",
  referred_participants: "Referred participants",
  approved_partner: "Approved partner",
  active_delivery: "Active delivery",
  completed: "Completed",
  long_term_partner: "Long-term partner",
};

const STAGE_COLOR = {
  new_enquiry: "bg-zinc-700 text-zinc-100",
  under_review: "bg-sky-500/20 text-sky-300 border border-sky-500/40",
  discovery_booked: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40",
  awaiting_documents: "bg-orange-500/20 text-orange-300 border border-orange-500/40",
  funding_discussion: "bg-pink-500/20 text-pink-300 border border-pink-500/40",
  referred_participants: "bg-purple-500/20 text-purple-300 border border-purple-500/40",
  approved_partner: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  active_delivery: "bg-amber-400 text-black",
  completed: "bg-zinc-500/30 text-zinc-200 border border-zinc-500/40",
  long_term_partner: "bg-amber-400/30 text-amber-200 border border-amber-400/60",
};

export function StageBadge({ stage }) {
  const cls = STAGE_COLOR[stage] || "bg-zinc-700 text-zinc-100";
  return (
    <span
      data-testid={`stage-badge-${stage}`}
      className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold whitespace-nowrap ${cls}`}
    >
      {STAGE_LABEL[stage] || stage}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

import { authHeaders } from "@/lib/adminAuth";

export default function PartnershipDrawer({ partnership, meta, onClose, onUpdated }) {
  const [form, setForm] = useState(partnership);
  const [saving, setSaving] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => setForm(partnership), [partnership]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!form) return null;

  const headers = authHeaders();

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleNumber = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value === "" ? 0 : Number(value) }));
  };

  const toggleTag = (tag) => {
    setForm((f) => {
      const tags = f.programme_tags || [];
      return {
        ...f,
        programme_tags: tags.includes(tag)
          ? tags.filter((t) => t !== tag)
          : [...tags, tag],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        stage: form.stage,
        programme_tags: form.programme_tags || [],
        referred_participants_count: Number(form.referred_participants_count || 0),
        funding_value_gbp: Number(form.funding_value_gbp || 0),
        funding_currency: form.funding_currency || "GBP",
        organisation: form.organisation,
        organisation_type: form.organisation_type,
        contact_name: form.contact_name,
        role: form.role || null,
        email: form.email,
        phone: form.phone || null,
        location: form.location || null,
        participant_count: form.participant_count || null,
      };
      const res = await axios.patch(
        `${API}/admin/partnerships/${form.id}`,
        payload,
        { headers }
      );
      setForm(res.data);
      onUpdated?.(res.data);
      toast.success("Partnership saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setAddingNote(true);
    try {
      const res = await axios.post(
        `${API}/admin/partnerships/${form.id}/notes`,
        { body: noteBody.trim() },
        { headers }
      );
      setForm(res.data);
      onUpdated?.(res.data);
      setNoteBody("");
      toast.success("Note added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add note.");
    } finally {
      setAddingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const res = await axios.delete(
        `${API}/admin/partnerships/${form.id}/notes/${noteId}`,
        { headers }
      );
      setForm(res.data);
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete note.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/admin/partnerships/${form.id}/documents`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } }
      );
      setForm(res.data);
      onUpdated?.(res.data);
      toast.success("Document uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(
        `${API}/admin/partnerships/${form.id}/documents/${doc.id}`,
        { headers, responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed.");
    }
  };

  const deleteDoc = async (docId) => {
    try {
      const res = await axios.delete(
        `${API}/admin/partnerships/${form.id}/documents/${docId}`,
        { headers }
      );
      setForm(res.data);
      onUpdated?.(res.data);
      toast.success("Document removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  const stages = meta?.stages || Object.keys(STAGE_LABEL);
  const tags = meta?.programme_tags || [];
  const notes = [...(form.notes || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );
  const docs = form.documents || [];

  return (
    <div
      data-testid="partnership-drawer"
      className="fixed inset-0 z-[80] flex"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close drawer"
        data-testid="partnership-drawer-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      {/* Panel */}
      <div className="relative ml-auto h-full w-full max-w-3xl bg-zinc-950 border-l border-amber-400/20 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-white/5 px-6 py-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400">
              Partnership
            </p>
            <h2
              data-testid="partnership-drawer-title"
              className="mt-1 font-display font-black uppercase text-2xl truncate"
            >
              {form.organisation}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
              <StageBadge stage={form.stage || "new_enquiry"} />
              <span>· {form.organisation_type}</span>
              <span>· Opened {fmtDate(form.created_at)}</span>
              {form.updated_at && <span>· Updated {fmtDate(form.updated_at)}</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="partnership-drawer-close"
            className="border border-white/10 hover:border-amber-400 hover:text-amber-400 p-2 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-8 space-y-10">
          {/* Stage + tracker */}
          <section>
            <SectionLabel>Stage & tracker</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Stage</Label>
                <select
                  name="stage"
                  data-testid="partnership-stage-select"
                  value={form.stage || "new_enquiry"}
                  onChange={handleField}
                  className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400"
                >
                  {stages.map((s) => (
                    <option key={s} value={s}>
                      {STAGE_LABEL[s] || s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Referred participants</Label>
                <input
                  type="number"
                  min={0}
                  name="referred_participants_count"
                  data-testid="partnership-participants-input"
                  value={form.referred_participants_count ?? 0}
                  onChange={handleNumber}
                  className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <Label>Funding value</Label>
                <div className="mt-1 flex gap-2">
                  <select
                    name="funding_currency"
                    data-testid="partnership-currency-select"
                    value={form.funding_currency || "GBP"}
                    onChange={handleField}
                    className="bg-black border border-white/10 px-3 py-3 text-white focus:outline-none focus:border-amber-400"
                  >
                    <option>GBP</option>
                    <option>EUR</option>
                    <option>USD</option>
                  </select>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    name="funding_value_gbp"
                    data-testid="partnership-funding-input"
                    value={form.funding_value_gbp ?? 0}
                    onChange={handleNumber}
                    placeholder="0.00"
                    className="flex-1 bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
              <div>
                <Label>Engagement modes (from enquiry)</Label>
                <p className="mt-2 text-xs text-zinc-300">
                  {(form.engagement_modes || []).join(", ") || "—"}
                </p>
              </div>
            </div>
          </section>

          {/* Programme tags */}
          <section>
            <SectionLabel>Programme interest tags</SectionLabel>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="partnership-tags">
              {tags.map((tag) => {
                const on = (form.programme_tags || []).includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    data-testid={`partnership-tag-${tag.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    onClick={() => toggleTag(tag)}
                    className={`px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                      on
                        ? "bg-amber-400 text-black border-amber-400"
                        : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Contact details */}
          <section>
            <SectionLabel>Contact & organisation</SectionLabel>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <DrawerField label="Organisation" name="organisation" value={form.organisation || ""} onChange={handleField} testId="drawer-organisation" />
              <DrawerField label="Organisation type" name="organisation_type" value={form.organisation_type || ""} onChange={handleField} testId="drawer-org-type" />
              <DrawerField label="Contact name" name="contact_name" value={form.contact_name || ""} onChange={handleField} testId="drawer-contact-name" />
              <DrawerField label="Role" name="role" value={form.role || ""} onChange={handleField} testId="drawer-role" />
              <DrawerField label="Email" name="email" type="email" value={form.email || ""} onChange={handleField} testId="drawer-email" />
              <DrawerField label="Phone" name="phone" value={form.phone || ""} onChange={handleField} testId="drawer-phone" />
              <DrawerField label="Location" name="location" value={form.location || ""} onChange={handleField} testId="drawer-location" />
              <DrawerField label="Original participant note" name="participant_count" value={form.participant_count || ""} onChange={handleField} testId="drawer-participant-count" />
            </div>
            <div className="mt-4">
              <Label>Original enquiry message</Label>
              <p className="mt-2 text-sm text-zinc-300 whitespace-pre-wrap border border-white/5 bg-black p-4">
                {form.message}
              </p>
            </div>
          </section>

          {/* Save */}
          <button
            onClick={save}
            disabled={saving}
            data-testid="partnership-save-button"
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save changes"}
          </button>

          {/* Notes */}
          <section>
            <SectionLabel>
              <StickyNote className="h-4 w-4 inline-block mr-2 -mt-0.5" />
              Notes & activity log
            </SectionLabel>
            <div className="mt-4 border border-white/10 bg-black p-4">
              <textarea
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
                data-testid="partnership-note-input"
                rows={3}
                placeholder="Add a note — meeting outcome, next step, intel…"
                className="w-full bg-zinc-950 border border-white/10 p-3 text-sm text-white focus:outline-none focus:border-amber-400"
              />
              <button
                onClick={addNote}
                disabled={addingNote || !noteBody.trim()}
                data-testid="partnership-note-add"
                className="mt-3 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black px-5 py-2.5 font-bold uppercase tracking-[0.2em] text-[11px]"
              >
                {addingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add note
              </button>
            </div>
            <ul className="mt-5 space-y-3" data-testid="partnership-notes-list">
              {notes.length === 0 && (
                <li className="text-zinc-500 text-sm">No notes yet.</li>
              )}
              {notes.map((n) => (
                <li
                  key={n.id}
                  data-testid={`partnership-note-${n.id}`}
                  className="border border-white/5 bg-zinc-950 p-4"
                >
                  <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.22em] font-bold">
                    <span className="text-amber-400">{n.author || "TOK Team"}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 normal-case tracking-normal text-xs">
                        {fmtDate(n.created_at)}
                      </span>
                      <button
                        onClick={() => deleteNote(n.id)}
                        data-testid={`partnership-note-delete-${n.id}`}
                        className="text-zinc-500 hover:text-red-400 transition-colors"
                        aria-label="Delete note"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-zinc-200 whitespace-pre-wrap">{n.body}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* Documents */}
          <section>
            <SectionLabel>
              <FileText className="h-4 w-4 inline-block mr-2 -mt-0.5" />
              Documents
            </SectionLabel>
            <div className="mt-4">
              <input
                ref={fileRef}
                type="file"
                onChange={onUpload}
                data-testid="partnership-doc-input"
                className="hidden"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="partnership-doc-upload-button"
                className="inline-flex items-center gap-2 border border-white/10 hover:border-amber-400 hover:text-amber-400 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading ? "Uploading…" : "Upload document"}
              </button>
              <p className="mt-2 text-[11px] text-zinc-500">
                PDF, Word, Excel, images. Max 15 MB.
              </p>
            </div>
            <ul className="mt-5 space-y-2" data-testid="partnership-docs-list">
              {docs.length === 0 && (
                <li className="text-zinc-500 text-sm">No documents uploaded yet.</li>
              )}
              {docs.map((d) => (
                <li
                  key={d.id}
                  data-testid={`partnership-doc-${d.id}`}
                  className="flex items-center justify-between gap-3 border border-white/5 bg-zinc-950 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100 truncate" title={d.filename}>
                      {d.filename}
                    </p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">
                      {fmtBytes(d.size_bytes)} · {fmtDate(d.uploaded_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => downloadDoc(d)}
                      data-testid={`partnership-doc-download-${d.id}`}
                      className="border border-white/10 hover:border-amber-400 hover:text-amber-400 p-2 transition-colors"
                      aria-label="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteDoc(d.id)}
                      data-testid={`partnership-doc-delete-${d.id}`}
                      className="border border-white/10 hover:border-red-400 hover:text-red-400 p-2 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400 border-b border-white/5 pb-3">
      {children}
    </p>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-400">
      {children}
    </label>
  );
}

function DrawerField({ label, name, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-1 w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400"
      />
    </div>
  );
}
